import gql from 'graphql-tag';
import React, { useCallback } from 'react';
import { flowRight } from 'lodash';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  CheckoutOrderSummaryFields,
  CoreCheckoutUpdateOrderQuantity,
  CoreCheckoutApplyDiscountCode,
  CoreCheckoutSelectOrderShippingMethod,
} from '../schemas/rql';
import { useMutation } from '../lib/use_mutation_hook';
import CheckoutOrderSummaryLineItems, { CheckoutOrderSummaryLineItemsFragment } from './checkout_order_summary_line_items';
import CheckoutOrderSummaryOrderItem, { CheckoutOrderSummaryOrderItemFragment } from './checkout_order_summary_order_item';
import LoadingOverlay from '../shared/loading_overlay';
import CheckoutDiscountCodeForm from './checkout_discount_code_form';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import { useCheckoutFormDisplay } from './checkout_form_display_context';

type UpdateOrderQuantityMutationFn = MutationFn<CoreCheckoutUpdateOrderQuantity.Mutation, CoreCheckoutUpdateOrderQuantity.Variables>;
type ApplyCheckoutDiscountCodeMutationFn = MutationFn<
CoreCheckoutApplyDiscountCode.Mutation,
CoreCheckoutApplyDiscountCode.Variables
>;
type SelectShippingMethodMutationFn = MutationFn<
CoreCheckoutSelectOrderShippingMethod.Mutation,
CoreCheckoutSelectOrderShippingMethod.Variables
>;

type Checkout = CheckoutOrderSummaryFields.Fragment;

interface ExternalProps {
  checkout: Checkout;
}

interface MutationProps {
  updateOrderQuantity: UpdateOrderQuantityMutationFn;
  selectShippingMethod: SelectShippingMethodMutationFn;
  applyCheckoutDiscountCode: ApplyCheckoutDiscountCodeMutationFn;
}

type Props = ExternalProps & MutationProps;

export function CheckoutOrderSummary(props: Props) {
  const onOrderQuantitySelect = useOrderQuantitySelect(
    props.checkout.id,
    props.updateOrderQuantity,
  );

  const onShippingMethodSelect = useShippingMethodSelect(
    props.checkout.id,
    props.selectShippingMethod,
  );

  const singlePageCheckoutExpEnabled = useExpEnabled(experiments.SINGLE_PAGE_CHECKOUT);
  const { submitDiscountCode, errors } = useDiscountCode(
    props.checkout.id,
    props.applyCheckoutDiscountCode,
  );

  const { isLoading } = useCheckoutFormDisplay();

  return (
    <LoadingOverlay showOverlay={isLoading}>
      <div className="checkout-order-summary">
        <ul>
          {
            props.checkout.orders.map((order) => (
              <li key={order.listingId} className="checkout-order-summary__item">
                <CheckoutOrderSummaryOrderItem
                  order={order}
                  actionsDisabled={isLoading}
                  onQuantitySelect={onOrderQuantitySelect}
                  onShippingMethodSelect={onShippingMethodSelect}
                />
              </li>
            ))
          }
        </ul>
        {singlePageCheckoutExpEnabled &&
          <>
            <div className="bdt-1 bd-color-primary mtb-1" />
            <CheckoutDiscountCodeForm
              submitDiscountCode={submitDiscountCode}
              selectedPaymentMethod={props.checkout.paymentMethod.type}
              disableSubmitButton={isLoading}
              errors={errors}
            />
          </>
        }
        <div className="bdt-1 bd-color-primary mtb-1" />
        <CheckoutOrderSummaryLineItems lineItems={props.checkout.lineItems} />
      </div>
    </LoadingOverlay>
  );
}

function useOrderQuantitySelect(
  checkoutId: string,
  updateOrderQuantityMutation: UpdateOrderQuantityMutationFn,
) {
  const [updateOrderQuantity] = useMutation(updateOrderQuantityMutation);
  const { setLoading } = useCheckoutFormDisplay();

  const onQuantitySelect = useCallback(async ({ listingId, quantity }) => {
    setLoading(true);
    await updateOrderQuantity({
      variables: {
        checkoutId,
        listingId,
        quantity,
      },
    });
    setLoading(false);
  }, [checkoutId, updateOrderQuantity, setLoading]);

  return onQuantitySelect;
}

function useShippingMethodSelect(
  checkoutId: string,
  selectShippingMethodMutation: SelectShippingMethodMutationFn,
) {
  const [selectShippingMethod] = useMutation(selectShippingMethodMutation);
  const { setLoading } = useCheckoutFormDisplay();

  const onShippingMethodSelect = useCallback(async ({ listingId, shippingMethodType }) => {
    setLoading(true);
    await selectShippingMethod({
      variables: {
        input: {
          id: checkoutId,
          order: {
            listingId,
            shippingMethodType,
          },
        },
      },
    });
    setLoading(false);
  }, [checkoutId, selectShippingMethod, setLoading]);

  return onShippingMethodSelect;
}

function useDiscountCode(
  checkoutId: string,
  applyCheckoutDiscountCodeMutation: ApplyCheckoutDiscountCodeMutationFn,
) {
  const [applyCheckoutDiscountCode, { errors }] = useMutation(applyCheckoutDiscountCodeMutation);
  const { setLoading } = useCheckoutFormDisplay();

  const submitDiscountCode = useCallback(async (discountCode: string) => {
    setLoading(true);
    await applyCheckoutDiscountCode({
      variables: {
        input: {
          discountCode,
          id: checkoutId,
        },
      },
    });
    setLoading(false);
  }, [checkoutId, applyCheckoutDiscountCode, setLoading]);

  return { submitDiscountCode, errors };
}

export const CheckoutOrderSummaryFragment = gql`
  fragment CheckoutOrderSummaryFields on Checkout {
    _id
    id
    orders {
      listingId
    }
    paymentMethod {
      type
      isComplete
    }
    ...CheckoutOrderSummaryOrderItemFields
    ...CheckoutOrderSummaryLineItemsFields
  }
  ${CheckoutOrderSummaryOrderItemFragment}
  ${CheckoutOrderSummaryLineItemsFragment}
`;

const withOrderQuantityMutation = withGraphql<
ExternalProps,
CoreCheckoutUpdateOrderQuantity.Mutation,
CoreCheckoutUpdateOrderQuantity.Variables
>(
  gql`
    mutation  Core_Checkout_UpdateOrderQuantity(
      $checkoutId: String,
      $listingId: String,
      $quantity: Int
    ) {
      editCheckoutCart(
        input: {
          id: $checkoutId
          order: {
            listingId: $listingId,
            quantity: $quantity
          }
        }
      ) {
        checkout {
          _id
          ...CheckoutOrderSummaryFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
  `,
  {
    name: 'updateOrderQuantity',
  },
);

const withDiscountCodeMutation = withGraphql<
ExternalProps,
CoreCheckoutApplyDiscountCode.Mutation,
CoreCheckoutApplyDiscountCode.Variables
>(
  gql`
    mutation Core_Checkout_ApplyDiscountCode($input: Input_core_apimessages_ApplyCheckoutDiscountCodeRequest) {
      applyCheckoutDiscountCode(input: $input) {
        checkout {
          _id
          id
          ...CheckoutOrderSummaryFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
  `,
  {
    name: 'applyCheckoutDiscountCode',
  },
);

const withShippingMethodMutation = withGraphql<
ExternalProps,
CoreCheckoutSelectOrderShippingMethod.Mutation,
CoreCheckoutSelectOrderShippingMethod.Variables
>(
  gql`
    mutation Core_Checkout_SelectOrderShippingMethod($input: Input_core_apimessages_CheckoutSelectOrderShippingMethodRequest) {
      selectCheckoutOrderShippingMethod(input: $input) {
        checkout {
          _id
          id
          orders {
            listingId
            shippingMethod {
              name
              type
            }
          }
          ...CheckoutOrderSummaryFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
  `,
  {
    name: 'selectShippingMethod',
  },
);

const connected: React.FunctionComponent<ExternalProps> = flowRight([
  withOrderQuantityMutation,
  withDiscountCodeMutation,
  withShippingMethodMutation,
])(CheckoutOrderSummary);

export default connected;
